import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Save, CheckCircle, AlertCircle, Plus, Trash2, Upload, Eye, EyeOff, Camera } from 'lucide-react';
import 'react-phone-number-input/style.css'
import PhoneInput from 'react-phone-number-input'
import { countryPhoneCodes } from '../data/countryCodes';
import { useAuth } from '../context/AuthContext';
import { handlePrintToPDF } from '../utils/prints';

const PersonalDataForm = ({eid, data}) => {
  const { user, logout, checkAuthStatus } = useAuth();
  const shouldDisableInput = user.filledForms.includes("Personal")

  
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({});
  const [children, setChildren] = useState([
    { id: 1, name: '', dob: '', education: '', occupation: '', location: '', remarks: '', deceased: false },
    { id: 2, name: '', dob: '', education: '', occupation: '', location: '', remarks: '', deceased: false }
  ]);
  const [experiences, setExperiences] = useState([
    { id: 1, company: '', position: '', startDate: '', endDate: '', salary: '', reasonForLeaving: '', responsibilities: '', industry: '' }
  ]);
  const [references, setReferences] = useState([
    { id: 1, name: '', position: '', company: '', phone: '', email: '', relationship: '' },
    { id: 2, name: '', position: '', company: '', phone: '', email: '', relationship: '' }
  ]);
  const [sameAsPermanent, setSameAsPermanent] = useState(false);
  const [isAutoSaving, setIsAutoSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [countryCode, setCountryCode] = useState('+91');
  const [alternateCountryCode, setAlternateCountryCode] = useState('+91');
  const [emergencyCountryCode, setEmergencyCountryCode] = useState('+91');
  
  const totalSteps = 8;

  const indianBanks = [
    'State Bank of India',
    'HDFC Bank',
    'ICICI Bank',
    'Axis Bank',
    'Bank of Baroda',
    'Punjab National Bank',
    'Canara Bank',
    'Union Bank of India',
    'Bank of India',
    'Indian Bank',
    'Central Bank of India',
    'UCO Bank',
    'Indian Overseas Bank',
    'Punjab And Sind Bank',
    'Kotak Mahindra Bank',
    'IndusInd Bank',
    'Yes Bank',
    'IDFC First Bank',
    'Federal Bank',
    'South Indian Bank',
    'Karnataka Bank',
    'City Union Bank',
    'Jammu and Kashmir Bank',
    'DCB Bank',
    'RBL Bank',
    'Other'
  ];

  const stepNames = [
    'Personal Info',
    'Contact Details',
    'Family Details',
    'Education',
    'Experience',
    'References',
    'Declaration',
    'Review'
  ];



  // Auto-save functionality
  const saveToStorage = useCallback(() => {
    setIsAutoSaving(true);
    const dataToSave = {
      formData,
      children,
      experiences,
      references,
      currentStep,
      sameAsPermanent,
      countryCode,
      alternateCountryCode,
      emergencyCountryCode,
      timestamp: new Date().toISOString()
    };
    
    // Store in memory (simulating localStorage functionality)
    localStorage.setItem('formAutoSaveData', JSON.stringify(dataToSave));
    
    setTimeout(() => {
      setIsAutoSaving(false);
      setLastSaved(new Date());
      if (currentStep !== totalSteps && !eid) {
        submitForm()
      }
    }, 500);
  }, [formData, children, experiences, references, currentStep, sameAsPermanent, countryCode, alternateCountryCode, emergencyCountryCode]);

  // Load saved data on component mount
  useEffect(() => {
    // if (localStorage.getItem('formAutoSaveData')) {
    //   const saved = JSON.parse(localStorage.getItem('formAutoSaveData'));
    //   setFormData(saved.formData || {});
    //   setChildren(saved.children || [{ id: 1, name: '', age: '', education: '', occupation: '', location: '', remarks: '', deceased: false }]);
    //   setExperiences(saved.experiences || [{ id: 1, company: '', position: '', startDate: '', endDate: '', salary: '', reasonForLeaving: '', responsibilities: '', industry: '' }]);
    //   setReferences(saved.references || [{ id: 1, name: '', position: '', company: '', phone: '', email: '', relationship: '' }]);
    //   setCurrentStep(saved.currentStep || 1);
    //   setSameAsPermanent(saved.sameAsPermanent || false);
    //   setCountryCode(saved.countryCode || '+91');
    //   setAlternateCountryCode(saved.alternateCountryCode || '+91');
    //   setEmergencyCountryCode(saved.emergencyCountryCode || '+91');
    //   setLastSaved(new Date(saved.timestamp));
    // }
  }, []);

  // Auto-save every 3 seconds when form data changes
  useEffect(() => {
    const timer = setTimeout(() => {
      if (Object.keys(formData).length > 0) {
        saveToStorage();
      }
    }, 3000);

    return () => clearTimeout(timer);
  }, [formData, saveToStorage]);

  // Handle same as permanent address
  useEffect(() => {
    if (sameAsPermanent) {
      setFormData(prev => ({
        ...prev,
        presentAddress: prev.permanentAddress || '',
        presentPinCode: prev.permanentPinCode || '',
        presentCity: prev.permanentCity || '',
        presentVillage: prev.permanentVillage || '',
        presentPhone: prev.permanentPhone || ''
      }));
    }
  }, [sameAsPermanent, formData.permanentAddress, formData.permanentPinCode, formData.permanentCity, formData.permanentVillage, formData.permanentPhone]);

  // Validation functions
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone) => {
    const phoneRegex = /^[0-9]{10}$/;
    return phoneRegex.test(phone.replace(/\D/g, ''));
  };

  const validatePAN = (pan) => {
    const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
    return panRegex.test(pan);
  };

  const validateAadhaar = (aadhaar) => {
    const aadhaarRegex = /^[0-9]{12}$/;
    return aadhaarRegex.test(aadhaar.replace(/\D/g, ''));
  };

  const validateAge = (dob) => {
    if (!dob) return true;
    const today = new Date();
    const birthDate = new Date(dob);
    const age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      return age >= 17; // Allow 17+ but will show warning for under 18
    }
    return age >= 17;
  };

  const validatePinCode = (pinCode) => {
    const pinRegex = /^[0-9]{6}$/;
    return pinRegex.test(pinCode);
  };

  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    let processedValue = type === 'checkbox' ? checked : value;

    // Input restrictions and formatting
    if (name === 'mobile' || name === 'presentPhone' || name === 'permanentPhone' || name === 'emergencyPhone' || name === 'alternatePhone') {
      processedValue = value.replace(/\D/g, '').slice(0, 10);
    } else if (name === 'age' || name === 'height' || name === 'weight' || name.includes('Age')) {
      processedValue = value.replace(/\D/g, '').slice(0, 3);
    } else if (name === 'panNo') {
      processedValue = value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 10);
    } else if (name === 'aadhaarNo') {
      processedValue = value.replace(/\D/g, '').slice(0, 12);
    } else if (name === 'firstName' || name === 'middleName' || name === 'surname' || name.includes('Name')) {
      processedValue = value.replace(/[^a-zA-Z\s.]/g, '').slice(0, 50);
    } else if (name === 'pinCode' || name === 'permanentPinCode' || name === 'presentPinCode') {
      processedValue = value.replace(/\D/g, '').slice(0, 6);
    } else if (name === 'city' || name === 'village' || name === 'permanentCity' || name === 'permanentVillage' || name === 'presentCity' || name === 'presentVillage') {
      processedValue = value.replace(/[^a-zA-Z\s.]/g, '').slice(0, 50);
    }

    setFormData(prev => ({
      ...prev,
      [name]: processedValue
    }));

    // Clear validation error when user starts typing
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  }, [validationErrors]);

  const handleChildChange = useCallback((id, field, value) => {
    let processedValue = value;
    
    if (field === 'age') {
      processedValue = value.replace(/\D/g, '').slice(0, 3);
    } else if (field === 'name') {
      processedValue = value.replace(/[^a-zA-Z\s]/g, '').slice(0, 50);
    }

    setChildren(prev => prev.map(child => 
      child.id === id ? { ...child, [field]: processedValue } : child
    ));
  }, []);

  const handleExperienceChange = useCallback((id, field, value) => {
    let processedValue = value;
    
    if (field === 'salary') {
      processedValue = value.replace(/\D/g, '');
    }

    setExperiences(prev => prev.map(exp => 
      exp.id === id ? { ...exp, [field]: processedValue } : exp
    ));
  }, []);

  const handleReferenceChange = useCallback((id, field, value) => {
    let processedValue = value;
    
    if (field === 'phone') {
      processedValue = value.replace(/\D/g, '').slice(0, 10);
    } else if (field === 'name') {
      processedValue = value.replace(/[^a-zA-Z\s]/g, '').slice(0, 50);
    }

    setReferences(prev => prev.map(ref => 
      ref.id === id ? { ...ref, [field]: processedValue } : ref
    ));
  }, []);

  const addChild = () => {
    const newId = Math.max(...children.map(c => c.id), 0) + 1;
    setChildren(prev => [...prev, {
      id: newId,
      name: '',
      age: '',
      education: '',
      occupation: '',
      location: '',
      remarks: '',
      deceased: false
    }]);
  };

  const removeChild = (id) => {
    if (children.length > 1) {
      setChildren(prev => prev.filter(child => child.id !== id));
    }
  };

  const addExperience = () => {
    const newId = Math.max(...experiences.map(e => e.id), 0) + 1;
    setExperiences(prev => [...prev, {
      id: newId,
      company: '',
      position: '',
      startDate: '',
      endDate: '',
      salary: '',
      reasonForLeaving: '',
      responsibilities: '',
      industry: ''
    }]);
  };

  const removeExperience = (id) => {
    if (experiences.length > 1) {
      setExperiences(prev => prev.filter(exp => exp.id !== id));
    }
  };

// Updated add reference functions
const addProfessionalReference = () => {
  // Find the highest odd ID and add 2, or start with 1 if no odd IDs exist
  const oddIds = references.filter(r => r.id % 2 === 1).map(r => r.id);
  const newId = oddIds.length > 0 ? Math.max(...oddIds) + 2 : 1;
  
  setReferences(prev => [...prev, {
    id: newId,
    type: 'professional',
    name: '',
    position: '',
    company: '',
    phone: '',
    email: '',
    relationship: ''
  }]);
};

const addPersonalReference = () => {
  // Find the highest even ID and add 2, or start with 2 if no even IDs exist
  const evenIds = references.filter(r => r.id % 2 === 0).map(r => r.id);
  const newId = evenIds.length > 0 ? Math.max(...evenIds) + 2 : 2;
  
  setReferences(prev => [...prev, {
    id: newId,
    type: 'personal',
    name: '',
    position: '',
    company: '',
    phone: '',
    email: '',
    relationship: ''
  }]);
};

// Helper functions to separate references
const professionalReferences = references.filter(ref => ref.id % 2 === 1);
const personalReferences = references.filter(ref => ref.id % 2 === 0);

  const removeReference = (id) => {
    if (references.length > 1) {
      setReferences(prev => prev.filter(ref => ref.id !== id));
    }
  };

  const validateStep = (step) => {
    const errors = {};
    
    if (step === 1) {
      if (!formData.firstName?.trim()) errors.firstName = 'First Name is required';
      if (!formData.surname?.trim()) errors.surname = 'Surname is required';
      if (!formData.dobISO) errors.dob = 'Date of Birth is required';
      else if (!validateAge(formData.dobISO)) errors.dob = 'You must be at least 18 years old';
      if (!formData.age) errors.age = 'Age is required';
      if (!formData.maritalStatus) errors.maritalStatus = 'Marital Status is required';
      if (formData.panNo && !validatePAN(formData.panNo)) errors.panNo = 'Invalid PAN format';
      if (formData.aadhaarNo && !validateAadhaar(formData.aadhaarNo)) errors.aadhaarNo = 'Invalid Aadhaar format';
    } else if (step === 2) {
      if (!formData.presentAddress?.trim()) errors.presentAddress = 'Present Address is required';
      if (!formData.presentPinCode?.trim()) errors.presentPinCode = 'Present Pin Code is required';
      else if (!validatePinCode(formData.presentPinCode)) errors.presentPinCode = 'Invalid Pin Code';
      if (!formData.presentCity?.trim()) errors.presentCity = 'Present City is required';
      if (!formData.mobile?.trim()) errors.mobile = 'Mobile number is required';
      else if (!validatePhone(formData.mobile)) errors.mobile = 'Invalid mobile number';
      if (!formData.email?.trim()) errors.email = 'Email is required';
      else if (!validateEmail(formData.email)) errors.email = 'Invalid email format';
      if (!formData.emergencyName?.trim()) errors.emergencyName = 'Emergency contact name is required';
      if (!formData.emergencyPhone?.trim()) errors.emergencyPhone = 'Emergency contact phone is required';
      else if (!validatePhone(formData.emergencyPhone)) errors.emergencyPhone = 'Invalid emergency contact phone';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const changeStep = async (direction) => {
    if (direction === 1 && currentStep < totalSteps) {
      // if (validateStep(currentStep)) {
        // console.log(direction, currentStep < totalSteps)
        if (currentStep === totalSteps - 1 && validateStep(1) && validateStep(2)) {
          saveToStorage();
          if(!formData.declaration1 || !formData.declaration2 || !formData.declaration3) {
            alert("Please accept all declarations");
            return;
          }
          await submitForm("Final");
          return;
        }
        if (currentStep === totalSteps - 1 && !validateStep(1)) {
          setCurrentStep(1);
          return;
        }
        if (currentStep === totalSteps - 1 && !validateStep(2)) {
          setCurrentStep(2);
          return;
        }
        setCurrentStep(prev => prev + 1);
      // }
    } else if (direction === -1 && currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };
  const [finalData, setFinalData] = useState({});

  const submitForm = async (type) => {
    const finalData = {
      eid: !eid?user.eid:eid,
      applicationId: `KCL${new Date().getFullYear()}${String(Math.floor(Math.random() * 10000)).padStart(4, '0')}`,
      formType: "Personal",
      submissionDate: new Date().toISOString(),
      submitType: type,
      data: JSON.parse(localStorage.getItem('formAutoSaveData'))
    };
    if (!finalData.data){
      console.log("No data to submit");
      return;
    }

    await fetch(`${process.env.REACT_APP_PUBLIC_API_BASE_URL}/v1/employees/save-application`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${localStorage.getItem('access_token')}`,
      },
      body: JSON.stringify(finalData),
    });
    setFinalData(finalData);
    // Clear auto-save data upon successful submission
    if(type === "Final"){
      checkAuthStatus();
      setCurrentStep(totalSteps);
      localStorage.removeItem('formAutoSaveData');
    }
  };

  const calculateAge = (dob) => {
    if (!dob) return '';
    const today = new Date();
    const birthDate = new Date(dob);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    
    return age;
  };

  const formatDateToDDMMYYYY = (isoDate) => {
    if (!isoDate) return '';
    const date = new Date(isoDate);
    const day = String(date.getDate()).padStart(2, '0');
    const month = date.toLocaleDateString('en-GB', { month: 'short' });
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const handleDobChange = useCallback((e) => {
    const isoDate = e.target.value;
    if (!isoDate) {
      setFormData(prev => ({ ...prev, dob: '', dobISO: '', age: '' }));
      return;
    }
    
    const formatted = formatDateToDDMMYYYY(isoDate);
    const age = calculateAge(isoDate);
    
    setFormData(prev => ({
      ...prev,
      dob: formatted,
      dobISO: isoDate,
      age: age.toString()
    }));
  }, []);

  const getEmployeeData = async (eid) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_PUBLIC_API_BASE_URL}/v1/employees/get-application?eid=${eid}&formType=Personal`, {
        headers: {
          "Authorization": `Bearer ${localStorage.getItem('access_token')}`,
        },
      });
      const data = await response.json();
      return data;
    } catch (error) {
      console.error(error);
      return null;
    }
  }
  const [errors, setErrors] = useState({});
  const [photographPreview, setPhotographPreview] = useState(null);
  const API_BASE_URL = `${process.env.REACT_APP_PUBLIC_API_BASE_URL}/v1/emp-documnets/docs`;
  const handleFileUpload = async (docType, event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      // Create filename with eid prefix
      const serverFilename = `${!eid ? user.eid : eid}_${docType}`;
      
      // Create FormData for file upload
      const formData = new FormData();
      
      // Rename the file before uploading
      const renamedFile = new File([file], serverFilename, { type: file.type });
      formData.append('file', renamedFile);

      // Upload to API
      const response = await fetch(`${API_BASE_URL}?eid=${!eid ? user.eid : eid}`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Upload failed');
      }

      // Refresh the documents list to get the new URL
      // await fetchUploadedDocuments();

    } catch (error) {
      console.error('Upload error:', error);
    } finally {
      event.target.value = ''; // Reset file input
    }
  };
  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file size (5MB = 5 * 1024 * 1024 bytes)
    if (file.size > 5 * 1024 * 1024) {
      setErrors(prev => ({
        ...prev,
        photograph: 'File size must be less than 5MB. Please upload a smaller file.'
      }));
      e.target.value = '';
      return;
    }
    
    // Check file type
    if (!file.type.startsWith('image/')) {
      setErrors(prev => ({
        ...prev,
        photograph: 'Please upload an image file (PNG, JPG, etc.)'
      }));
      e.target.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setPhotographPreview(reader.result);
      setFormData(prev => ({
        ...prev,
        photograph: reader.result
      }));
    };
    reader.readAsDataURL(file);
    
    if (errors.photograph) {
      setErrors(prev => ({
        ...prev,
        photograph: ''
      }));
    }
    await handleFileUpload('photograph', e);
  };
  const getDocsUrls = async (eid)=> {
    try {
      const response = await fetch(
        `${API_BASE_URL}/get-unused-urls?eid=${eid}`
      );

      if (!response.ok) {
        throw new Error('Failed to fetch documents');
      }

      const urlsData = await response.json();
      console.log(urlsData);
      return urlsData.urls;
    } catch (error) {
      console.error('Error fetching documents:', error);
      return null;
    }
  }
  const fetchAndSetSignatures = async (eid) => {
    try {
      const urls = await getDocsUrls(eid ? eid : user.eid); // returns an array of URLs
  
      // Define mapping between keyword and state key
      const signatureMap = {
        photograph: "photograph"
      };
  
      // Loop through each signature type and try to find a matching URL
      Object.entries(signatureMap).forEach(async ([key, value]) => {
        const match = urls.find(url => url.toLowerCase().includes(key.toLowerCase()));
  
        if (match) {
          // Fetch and convert to Base64 Data URL
          const response = await fetch(match);
          const blob = await response.blob();
          const reader = new FileReader();
          reader.onloadend = () => {
            setPhotographPreview(reader.result);
          };
          reader.readAsDataURL(blob);
        }
      });
    } catch (error) {
      console.error("Error loading signature previews:", error);
    }
  };

  const removePhotograph = () => {
    setPhotographPreview(null);
    setFormData(prev => ({
      ...prev,
      photograph: null
    }));
    // Reset file input
    const fileInput = document.getElementById('photograph');
    if (fileInput) fileInput.value = '';
  };

  function extractEmployeeCore(finalData) {
    if (!finalData) return null;
  
    return {
      name: finalData.personalInfo?.firstName || "",     // or full_name if you prefer
      age: finalData.personalInfo?.age || "",
      dob: finalData.personalInfo?.dateOfBirth || "",
      gender: finalData.personalInfo?.gender || "",
      bloodGroup: finalData.personalInfo?.bloodGroup || "",
      pan: finalData.personalInfo?.panNo || "",
      aadhaar: finalData.personalInfo?.aadhaarNo || "",
      designation: finalData.personalInfo?.designation || "",
      maritalStatus: finalData.personalInfo?.maritalStatus || ""
    };
  }
  
  // check user exist or not
  useEffect(() => {
    if (!user) {
      logout();
      return;
    }
    if(eid && data){
      fetchAndSetSignatures(eid);
      localStorage.setItem('formAutoSaveData', JSON.stringify(data));
      setFormData(data.formData || {});
      setChildren(data.children || [{ id: 1, name: '', age: '', education: '', occupation: '', location: '', remarks: '', deceased: false }]);
      setExperiences(data.experiences || [{ id: 1, company: '', position: '', startDate: '', endDate: '', salary: '', reasonForLeaving: '', responsibilities: '', industry: '' }]);
      setReferences(data.references || [{ id: 1, name: '', position: '', company: '', phone: '', email: '', relationship: '' }]);
      setCurrentStep(data.currentStep || 1);
      setSameAsPermanent(data.sameAsPermanent || false);
      setCountryCode(data.countryCode || '+91');
      setAlternateCountryCode(data.alternateCountryCode || '+91');
      setEmergencyCountryCode(data.emergencyCountryCode || '+91');
      setLastSaved(new Date(data.timestamp));
      return;
    }
    getEmployeeData(user.eid).then(data => {
      if(data.data){
        // setFormData(data.data);
        localStorage.setItem('formAutoSaveData', JSON.stringify(data.data));
        setFormData(data.data.formData || {});
        setChildren(data.data.children || [{ id: 1, name: '', age: '', education: '', occupation: '', location: '', remarks: '', deceased: false }]);
        setExperiences(data.data.experiences || [{ id: 1, company: '', position: '', startDate: '', endDate: '', salary: '', reasonForLeaving: '', responsibilities: '', industry: '' }]);
        setReferences(data.data.references || [{ id: 1, name: '', position: '', company: '', phone: '', email: '', relationship: '' }]);
        setCurrentStep(1);
        setSameAsPermanent(data.data.sameAsPermanent || false);
        setCountryCode(data.data.countryCode || '+91');
        setAlternateCountryCode(data.data.alternateCountryCode || '+91');
        setEmergencyCountryCode(data.data.emergencyCountryCode || '+91');
        setLastSaved(new Date(data.data.timestamp));  
      }
    });
    fetchAndSetSignatures(user.eid);
  }, []);
  const printRef = useRef(null);
  const headerRef = useRef(null);
  const [printState, setPrintState] = useState(false);
  
  useEffect(() => {
    if (printState) {
      handlePrintToPDF(printRef, "Employee").then(() => {
        setPrintState(false);
      }).catch((error) => {
        console.error("Error generating PDF:", error);
        setPrintState(false);
        alert("Failed to generate PDF. Please try again.");
      });
    }
  }, [printState]);

  return (
    <div ref={printRef} className="min-h-screen bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 p-2 sm:p-4 lg:p-6" >
      <div className="max-w-6xl mx-auto bg-white rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div ref={headerRef} className="bg-gradient-to-r from-gray-800 to-gray-900 text-white p-4 sm:p-6 lg:p-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="text-center sm:text-left">
              {/* Responsive Logo Section */}
              <div className="flex flex-wrap justify-center sm:justify-start items-center gap-4 mb-3">
              <img src={`${process.env.PUBLIC_URL}/images/kajaria-logo.png`} alt="Logo 1" className="h-10 sm:h-12 object-contain" />
              <img src={`${process.env.PUBLIC_URL}/images/gresbond-logo.png`} alt="Logo 2" className="h-10 sm:h-12 object-contain" />
              <img src={`${process.env.PUBLIC_URL}/images/kerovit_logo.png`} alt="Logo 3" className="h-10 sm:h-12 object-contain" />
            </div>
            <p className="text-xs sm:text-sm opacity-90 mb-1">
              J-1/B-1 (Extn), Mohan Co-operative Industrial Estate, Mathura Road, New Delhi 110 044
            </p>
            <p className="text-xs sm:text-sm opacity-90">
              Phone: 91-11-26946409 | Website: www.kajariaceramics.com
            </p>
          </div>

          {/* Right Side: Auto-save / Last Saved Info */}
          <div className="flex flex-col items-center sm:items-end gap-2">
            {isAutoSaving && (
              <div className="flex items-center gap-2 text-blue-300 text-xs">
                <Save size={12} className="animate-pulse" />
                Auto-saving...
              </div>
            )}
            {lastSaved && !isAutoSaving && (
              <div className="flex items-center gap-2 text-green-300 text-xs">
                <CheckCircle size={12} />
                Saved {lastSaved.toLocaleTimeString()}
              </div>
            )}
          </div>
        </div>
      </div>


        {/* Progress Bar */}
        <div className="no-print bg-gray-200 h-2 mx-4 sm:mx-6 lg:mx-8 mt-4 sm:mt-6 lg:mt-8 rounded-full">
          <div 
            className="h-full bg-gradient-to-r from-indigo-500 to-purple-600 rounded-full transition-all duration-500"
            style={{ width: `${(currentStep / totalSteps) * 100}%` }}
          />
        </div>

        {/* Step Indicator */}
        <div className="no-print flex justify-between mx-4 sm:mx-6 lg:mx-8 my-4 sm:my-6 lg:my-8 overflow-x-auto">
          {stepNames.map((name, index) => (
            <div
              key={index}
              className={`flex-shrink-0 text-center p-2 mx-1 text-xs sm:text-sm transition-colors ${
                index + 1 === currentStep
                  ? 'text-indigo-600 font-bold bg-indigo-50 rounded-lg'
                  : index + 1 < currentStep
                  ? 'text-green-600 font-medium'
                  : 'text-gray-400'
              }`}
            >
              <div className="whitespace-nowrap">{name}</div>
            </div>
          ))}
        </div>

        {/* Form Container */}
        <div className="p-4 sm:p-6 lg:p-8">
          {/* Step 1: Personal Information */}
          {(currentStep === 1 || printState)&& (
            <div className="  space-y-6">
              <h2 className="text-xl sm:text-2xl font-semibold text-gray-800 mb-6 pb-2 border-b-2 border-indigo-500">Personal Information</h2>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <InputField label="First Name" name="firstName" required validationErrors={validationErrors} formData={formData} handleInputChange={handleInputChange} />
                <InputField label="Middle Name" name="middleName" validationErrors={validationErrors} formData={formData} handleInputChange={handleInputChange} />
                <InputField label="Surname" name="surname" required validationErrors={validationErrors} formData={formData} handleInputChange={handleInputChange} />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <InputField 
                  label="Date of Birth" 
                  name="dobInput" 
                  type="date" 
                  value={formData.dobISO || ''} 
                  required 
                  onChange={handleDobChange}
                  validationErrors={validationErrors} formData={formData} handleInputChange={handleInputChange} 
                />
                <InputField label="Age (Years)" name="age" type="number" required disabled validationErrors={validationErrors} formData={formData} handleInputChange={handleInputChange} />
                {/* <InputField label="Gender" name="gender" required validationErrors={validationErrors} formData={formData} handleInputChange={handleInputChange} /> */}
                <div>
                  <label htmlFor="gender" className='mb-8'>Gender</label>
                  <select name="gender" value={formData.gender || ''} onChange={handleInputChange}
                    className="w-full mt-3 px-3 py-2.5 border-2 border-gray-200 rounded-lg focus:border-indigo-500 focus:outline-none transition-all bg-gray-50 focus:bg-white text-sm"
                    required
                    validationErrors={validationErrors} formData={formData} handleInputChange={handleInputChange}
                  >
                    <option value="">Select Gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>

              {/* <div className='pdf-break h-0 w-0 p-0 m-0'></div> */}

              {formData.dob && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-blue-800 text-sm">
                    Date of Birth: <strong>{formData.dob}</strong>
                  </p>
                </div>
              )}

              {formData.dob && parseInt(formData.age) < 18 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <p className="text-yellow-800 text-sm">
                    ⚠️ You must be at least 18 years old to submit this application.
                  </p>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <InputField label="Height (cm)" name="height" type="number" min="100" max="250" validationErrors={validationErrors} formData={formData} handleInputChange={handleInputChange} />
                <InputField label="Weight (kg)" name="weight" type="number" min="30" max="200" validationErrors={validationErrors} formData={formData} handleInputChange={handleInputChange} />
                <div>
                  <label className="block text-gray-700 font-medium mb-2 text-sm">Blood Group</label>
                  <select
                    name="bloodGroup"
                    value={formData.bloodGroup || ''}
                    onChange={handleInputChange}
                    className="w-full px-3 mt-3 py-2.5 border-2 border-gray-200 rounded-lg focus:border-indigo-500 focus:outline-none transition-all bg-gray-50 focus:bg-white text-sm"
                  >
                    <option value="">Select Blood Group</option>
                    {['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'].map(group => (
                      <option key={group} value={group}>{group}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-gray-700 font-medium mb-3 text-sm">
                  Marital Status <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {['unmarried', 'married', 'widowed', 'Divorced'].map((status) => (
                    <label key={status} className="flex items-center space-x-2 p-2 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors">
                      <input
                        type="radio"
                        name="maritalStatus"
                        value={status}
                        disabled={shouldDisableInput}
                        checked={formData.maritalStatus === status}
                        onChange={handleInputChange}
                        className="w-4 h-4 text-indigo-600"
                        required
                      />
                      <span className="capitalize text-sm">{status}</span>
                    </label>
                  ))}
                </div>
                {validationErrors.maritalStatus && (
                  <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                    <AlertCircle size={12} />
                    {validationErrors.maritalStatus}
                  </p>
                )}
              </div>

              <div className=" grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <InputField label="PAN Number" name="panNo" placeholder="ABCDE1234F" maxLength="10" validationErrors={validationErrors} formData={formData} handleInputChange={handleInputChange} />
                <InputField label="Aadhaar Number" name="aadhaarNo" placeholder="123456789012" maxLength="12" validationErrors={validationErrors} formData={formData} handleInputChange={handleInputChange} />
                <InputField label="Passport Number" name="passportNo" validationErrors={validationErrors} formData={formData} handleInputChange={handleInputChange} />
              </div>

 {/* <div className='pdf-break h-0 w-0 p-0 m-0'></div> */}

              <div className=" grid grid-cols-1 sm:grid-cols-2 gap-4">
                <InputField label="Passport Expiry Date" name="passportExpiry" type="date" validationErrors={validationErrors} formData={formData} handleInputChange={handleInputChange} />
                <div></div>
              </div>

              <div>
                <label className="block text-gray-700 font-medium mb-2 text-sm">Upload Recent Photograph</label>
                  {!photographPreview ? (
                    <div className={`border-2 border-dashed rounded-lg p-6 transition-colors ${
                      errors.photograph 
                        ? 'border-red-300 bg-red-50' 
                        : 'border-gray-300 hover:border-blue-400 bg-gray-50'
                    }`}>
                      <input
                        type="file"
                        disabled={shouldDisableInput}
                        accept="image/*"
                        onChange={handleFileChange}
                        className="hidden"
                        id="photograph"
                      />
                      <label htmlFor="photograph" className="cursor-pointer block text-center">
                        <Camera className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                        <p className="text-sm text-gray-600 mb-1">
                          Click to upload photograph
                        </p>
                        <p className="text-xs text-gray-500">PNG, JPG up to 5MB</p>
                      </label>
                    </div>
                  ) : (
                    <div className="border-2 border-gray-300 rounded-lg p-4 bg-white">
                      <div className="flex justify-between items-start mb-2">
                        <p className="text-sm font-medium text-gray-700">Preview:</p>
                        <button
                          type="button"
                          disabled={shouldDisableInput}
                          onClick={removePhotograph}
                          className="text-red-600 hover:text-red-800 text-sm font-medium"
                        >
                          Remove
                        </button>
                      </div>
                      <div className="bg-gray-50 p-4 rounded-lg flex justify-center items-center">
                        <img
                          src={photographPreview}
                          alt="Passport Photograph"
                          className="max-h-64 max-w-full object-contain rounded-lg shadow-sm"
                        />
                      </div>
                    </div>
                  )}
              </div>
            </div>
          )}

          {/* <div className='pdf-break h-0 w-0 p-0 m-0'></div> */}


          {/* Step 2: Contact Details */}
          {(currentStep === 2 || printState) && (
            <div className="  space-y-6 ">
              <h2 className="text-xl sm:text-2xl font-semibold text-gray-800 mb-6 pb-2 border-b-2 border-indigo-500">Contact Information</h2>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <label className="block text-gray-700 font-medium mb-2 text-sm">Permanent Address</label>
                  <textarea
                    name="permanentAddress"
                    value={formData.permanentAddress || ''}
                    onChange={handleInputChange}
                    rows="3"
                    className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-lg focus:border-indigo-500 focus:outline-none transition-colors bg-gray-50 focus:bg-white resize-vertical text-sm"
                    placeholder="Enter your permanent address"
                  />
                </div>

                <div className="space-y-4">
                    <InputField 
                      label="Village/Area" 
                      name="permanentVillage" 
                      type="text"
                      placeholder="Area/Village name"
                      validationErrors={validationErrors} formData={formData} handleInputChange={handleInputChange}
                    />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <InputField 
                      label="City" 
                      name="permanentCity" 
                      type="text"
                      placeholder="New Delhi"
                      validationErrors={validationErrors} formData={formData} handleInputChange={handleInputChange}
                    />
                    <InputField 
                      label="Pin Code" 
                      name="permanentPinCode" 
                      type="text" 
                      maxLength="6" 
                      placeholder="110001"
                      validationErrors={validationErrors} formData={formData} handleInputChange={handleInputChange}
                    />
                  </div>
                  


                  <div className="flex gap-2">
                    <div className="w-24">
                      <label className="block text-gray-700 font-medium mb-2 text-sm">Code</label>
                      <select
                        value={countryCode}
                        onChange={(e) => setCountryCode(e.target.value)}
                        className="w-full px-2 py-2.5 border-2 border-gray-200 rounded-lg focus:border-indigo-500 focus:outline-none transition-colors bg-gray-50 focus:bg-white text-sm"
                      >
                        {countryPhoneCodes.map((code, index) => (
                          <option key={index} value={code.split(' ')[1]}>
                            {code.split(' ')[0]}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="flex-1">
                      <InputField 
                        label="Permanent Address Phone" 
                        name="permanentPhone" 
                        type="tel" 
                        maxLength="10" 
                        validationErrors={validationErrors} formData={formData} handleInputChange={handleInputChange}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-lg border border-blue-200">
                <label className="flex items-center space-x-2 mb-4">
                  <input
                    type="checkbox"
                    checked={sameAsPermanent}
                    onChange={(e) => setSameAsPermanent(e.target.checked)}
                    className="w-4 h-4 text-indigo-600 rounded"
                  />
                  <span className="font-medium text-sm">Present address same as permanent address</span>
                </label>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-gray-700 font-medium mb-2 text-sm">
                      Present Address <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      name="presentAddress"
                      value={formData.presentAddress || ''}
                      onChange={handleInputChange}
                      rows="3"
                      disabled={sameAsPermanent}
                      className={`w-full px-3 py-2.5 border-2 rounded-lg focus:outline-none transition-all resize-vertical text-sm ${
                        sameAsPermanent 
                          ? 'bg-gray-100 cursor-not-allowed border-gray-200' 
                          : validationErrors.presentAddress
                          ? 'border-red-300 focus:border-red-500 bg-red-50'
                          : 'border-gray-200 focus:border-indigo-500 bg-gray-50 focus:bg-white'
                      }`}
                      placeholder="Enter your current address"
                      required
                    />
                    {validationErrors.presentAddress && (
                      <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                        <AlertCircle size={12} />
                        {validationErrors.presentAddress}
                      </p>
                    )}
                  </div>

                  <div className="space-y-4">
                    
                    
                    <InputField 
                      label="Village/Area" 
                      name="presentVillage" 
                      type="text"
                      placeholder="Area/Village name"
                      disabled={sameAsPermanent}
                      className={sameAsPermanent ? 'opacity-75' : ''}
                      required
                      validationErrors={validationErrors} formData={formData} handleInputChange={handleInputChange}
                    />

                <div className=" grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <InputField 
                        label="City" 
                        name="presentCity" 
                        type="text"
                        placeholder="New Delhi"
                        disabled={sameAsPermanent}
                        className={sameAsPermanent ? 'opacity-75' : ''}
                        required
                        validationErrors={validationErrors} formData={formData} handleInputChange={handleInputChange}
                      />
                      <div>
                        <InputField 
                          label="Pin Code" 
                          name="presentPinCode" 
                          type="text" 
                          maxLength="6" 
                          placeholder="110001"
                          disabled={sameAsPermanent}
                          className={sameAsPermanent ? 'opacity-75' : ''}
                          required
                          validationErrors={validationErrors} formData={formData} handleInputChange={handleInputChange}
                        />
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <div className="w-24">
                        <label className="block text-gray-700 font-medium mb-2 text-sm">Code</label>
                        <select
                          value={countryCode}
                          onChange={(e) => setCountryCode(e.target.value)}
                          disabled={sameAsPermanent}
                          className={`w-full px-2 py-2.5 border-2 border-gray-200 rounded-lg focus:border-indigo-500 focus:outline-none transition-colors bg-gray-50 focus:bg-white text-sm ${sameAsPermanent ? 'opacity-75 cursor-not-allowed' : ''}`}
                        >
                          {countryPhoneCodes.map((code, index) => (
                            <option key={index} value={code.split(' ')[1]}>
                              {code.split(' ')[0]}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="flex-1">
                        <InputField 
                          label="Present Address Phone" 
                          name="presentPhone" 
                          type="tel" 
                          maxLength="10"
                          disabled={sameAsPermanent}
                          className={sameAsPermanent ? 'opacity-75' : ''}
                          required
                          validationErrors={validationErrors} formData={formData} handleInputChange={handleInputChange}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className=" grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex gap-2">
                  <div className="w-24">
                    <label className="block text-gray-700 font-medium mb-2 text-sm">Code</label>
                    <select
                      value={countryCode}
                      onChange={(e) => setCountryCode(e.target.value)}
                      className="w-full px-2 py-2.5 border-2 border-gray-200 rounded-lg focus:border-indigo-500 focus:outline-none transition-colors bg-gray-50 focus:bg-white text-sm"
                    >
                      {countryPhoneCodes.map((code, index) => (
                        <option key={index} value={code.split(' ')[1]}>
                          {code.split(' ')[0]}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex-1">
                    <InputField label="Mobile Number" name="mobile" type="tel" required maxLength="10" validationErrors={validationErrors} formData={formData} handleInputChange={handleInputChange} />
                  </div>
                </div>
                
                <InputField label="Email Address" name="email" type="email" required validationErrors={validationErrors} formData={formData} handleInputChange={handleInputChange} />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex gap-2">
                  <div className="w-24">
                    <label className="block text-gray-700 font-medium mb-2 text-sm">Code</label>
                    <select
                      value={alternateCountryCode}
                      onChange={(e) => setAlternateCountryCode(e.target.value)}
                      className="w-full px-2 py-2.5 border-2 border-gray-200 rounded-lg focus:border-indigo-500 focus:outline-none transition-colors bg-gray-50 focus:bg-white text-sm"
                    >
                      {countryPhoneCodes.map((code, index) => (
                        <option key={index} value={code.split(' ')[1]}>
                          {code.split(' ')[0]}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex-1">
                    <InputField label="Alternate Number (Optional)" name="alternatePhone" type="tel" maxLength="10" validationErrors={validationErrors} formData={formData} handleInputChange={handleInputChange} />
                  </div>
                </div>
                <div></div>
              </div>

              <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mt-8 mb-4">Emergency Contact</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <InputField label="Emergency Contact Name" name="emergencyName" required validationErrors={validationErrors} formData={formData} handleInputChange={handleInputChange}/>
                <div className="flex gap-2">
                  <div className="w-24">
                    <label className="block text-gray-700 font-medium mb-2 text-sm">Code</label>
                    <select
                      value={emergencyCountryCode}
                      onChange={(e) => setEmergencyCountryCode(e.target.value)}
                      className="w-full px-2 py-2.5 border-2 border-gray-200 rounded-lg focus:border-indigo-500 focus:outline-none transition-colors bg-gray-50 focus:bg-white text-sm"
                    >
                      {countryPhoneCodes.map((code, index) => (
                        <option key={index} value={code.split(' ')[1]}>
                          {code.split(' ')[0]}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex-1">
                    <InputField label="Emergency Contact Phone" name="emergencyPhone" type="tel" required maxLength="10" validationErrors={validationErrors} formData={formData} handleInputChange={handleInputChange} />
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-gray-700 font-medium mb-2 text-sm">Emergency Contact Address</label>
                <textarea
                  name="emergencyAddress"
                  value={formData.emergencyAddress || ''}
                  onChange={handleInputChange}
                  rows="3"
                  className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-lg focus:border-indigo-500 focus:outline-none transition-colors bg-gray-50 focus:bg-white resize-vertical text-sm"
                />
              </div>

              <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mt-8 mb-4">Bank Details</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <label className="block text-gray-700 font-medium mb-2 text-sm">Bank Name</label>
                  <select
                    name="bankName"
                    value={formData.bankName || ''}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-lg focus:border-indigo-500 focus:outline-none transition-colors bg-gray-50 focus:bg-white text-sm"
                  >
                    <option value="">Select Bank</option>
                    {indianBanks.map(bank => (
                      <option key={bank} value={bank}>{bank}</option>
                    ))}
                  </select>
                </div>
                
                {formData.bankName === 'Other' && (
                  <InputField label="Custom Bank Name" name="customBankName" placeholder="Enter bank name" validationErrors={validationErrors} formData={formData} handleInputChange={handleInputChange} />
                )}
                
                <InputField label="Account Number" name="accountNumber" validationErrors={validationErrors} formData={formData} handleInputChange={handleInputChange} />
                <InputField label="IFSC Code" name="ifscCode" placeholder="ABCD0123456" validationErrors={validationErrors} formData={formData} handleInputChange={handleInputChange} />
              </div>
            </div>
          )}

          {/* Step 3: Family Details */}
          {(currentStep === 3 || printState) && (
            <div className="pdf-break space-y-6 ">
              <h2 className="text-xl sm:text-2xl font-semibold text-gray-800 mb-6 pb-2 border-b-2 border-indigo-500">Family Details</h2>
              
              <div className="overflow-x-auto">
                <div className="min-w-full bg-white rounded-lg shadow-lg overflow-hidden">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white">
                        <th className="p-3 text-left font-semibold text-sm">Relationship</th>
                        <th className="p-3 text-left font-semibold text-sm">Name</th>
                        <th className="p-3 text-left font-semibold text-sm">Date of Birth</th>
                        <th className="p-3 text-left font-semibold text-sm">Education</th>
                        <th className="p-3 text-left font-semibold text-sm">Occupation</th>
                        <th className="p-3 text-left font-semibold text-sm">Location</th>
                        <th className="p-3 text-left font-semibold text-sm">Remarks</th>
                        <th className="p-3 text-left font-semibold text-sm">Deceased</th>
                        <th className="p-3 text-left font-semibold text-sm">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="p-2 font-medium text-sm">Spouse</td>
                        <td className="p-2"><input type="text" name="spouseName" value={formData.spouseName || ''} onChange={handleInputChange} className="w-full p-1.5 border border-gray-200 rounded text-xs focus:border-indigo-500 focus:outline-none" /></td>
                        <td className="p-2"><input type="date" name="spouseDOB" value={formData.spouseDOB || ''} onChange={handleInputChange} className="w-full p-1.5 border border-gray-200 rounded text-xs focus:border-indigo-500 focus:outline-none" max="100" /></td>
                        <td className="p-2"><input type="text" name="spouseEducation" value={formData.spouseEducation || ''} onChange={handleInputChange} className="w-full p-1.5 border border-gray-200 rounded text-xs focus:border-indigo-500 focus:outline-none" /></td>
                        <td className="p-2"><input type="text" name="spouseOccupation" value={formData.spouseOccupation || ''} onChange={handleInputChange} className="w-full p-1.5 border border-gray-200 rounded text-xs focus:border-indigo-500 focus:outline-none" /></td>
                        <td className="p-2"><input type="text" name="spouseLocation" value={formData.spouseLocation || ''} onChange={handleInputChange} className="w-full p-1.5 border border-gray-200 rounded text-xs focus:border-indigo-500 focus:outline-none" /></td>
                        <td className="p-2"><input type="text" name="spouseRemarks" value={formData.spouseRemarks || ''} onChange={handleInputChange} className="w-full p-1.5 border border-gray-200 rounded text-xs focus:border-indigo-500 focus:outline-none" /></td>
                        <td className="p-2 text-center">
                          <input 
                            type="checkbox" 
                            name="spouseDeceased" 
                            checked={formData.spouseDeceased || false}
                            onChange={handleInputChange}
                            className="w-4 h-4 text-red-600 rounded"
                          />
                        </td>
                        <td className="p-2"></td>
                      </tr>
                      {children.map((child) => (
                        <tr key={child.id} className="border-b border-gray-200 hover:bg-gray-50">
                          <td className="p-2 font-medium text-sm">Child {child.id}</td>
                          <td className="p-2"><input type="text" value={child.name} onChange={(e) => handleChildChange(child.id, 'name', e.target.value)} className="w-full p-1.5 border border-gray-200 rounded text-xs focus:border-indigo-500 focus:outline-none" /></td>
                          <td className="p-2"><input type="date" value={child.dob} onChange={(e) => handleChildChange(child.id, 'dob', e.target.value)} className="w-full p-1.5 border border-gray-200 rounded text-xs focus:border-indigo-500 focus:outline-none" max="30" /></td>
                          <td className="p-2"><input type="text" value={child.education} onChange={(e) => handleChildChange(child.id, 'education', e.target.value)} className="w-full p-1.5 border border-gray-200 rounded text-xs focus:border-indigo-500 focus:outline-none" /></td>
                          <td className="p-2"><input type="text" value={child.occupation} onChange={(e) => handleChildChange(child.id, 'occupation', e.target.value)} className="w-full p-1.5 border border-gray-200 rounded text-xs focus:border-indigo-500 focus:outline-none" /></td>
                          <td className="p-2"><input type="text" value={child.location} onChange={(e) => handleChildChange(child.id, 'location', e.target.value)} className="w-full p-1.5 border border-gray-200 rounded text-xs focus:border-indigo-500 focus:outline-none" /></td>
                          <td className="p-2"><input type="text" value={child.remarks} onChange={(e) => handleChildChange(child.id, 'remarks', e.target.value)} className="w-full p-1.5 border border-gray-200 rounded text-xs focus:border-indigo-500 focus:outline-none" /></td>
                          <td className="p-2 text-center">
                            <input 
                              type="checkbox" 
                              checked={child.deceased || false}
                              onChange={(e) => handleChildChange(child.id, 'deceased', e.target.checked)}
                              className="w-4 h-4 text-red-600 rounded"
                            />
                          </td>
                          <td className="p-2">
                            {children.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removeChild(child.id)}
                                className="text-red-600 hover:text-red-800 p-1"
                                title="Remove Child"
                              >
                                <Trash2 size={14} />
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="p-2 font-medium text-sm">Father</td>
                        <td className="p-2"><input type="text" name="fatherName" value={formData.fatherName || ''} onChange={handleInputChange} className="w-full p-1.5 border border-gray-200 rounded text-xs focus:border-indigo-500 focus:outline-none" /></td>
                        <td className="p-2"><input type="date" name="fatherDOB" value={formData.fatherDOB || ''} onChange={handleInputChange} className="w-full p-1.5 border border-gray-200 rounded text-xs focus:border-indigo-500 focus:outline-none" max="100" /></td>
                        <td className="p-2"><input type="text" name="fatherEducation" value={formData.fatherEducation || ''} onChange={handleInputChange} className="w-full p-1.5 border border-gray-200 rounded text-xs focus:border-indigo-500 focus:outline-none" /></td>
                        <td className="p-2"><input type="text" name="fatherOccupation" value={formData.fatherOccupation || ''} onChange={handleInputChange} className="w-full p-1.5 border border-gray-200 rounded text-xs focus:border-indigo-500 focus:outline-none" /></td>
                        <td className="p-2"><input type="text" name="fatherLocation" value={formData.fatherLocation || ''} onChange={handleInputChange} className="w-full p-1.5 border border-gray-200 rounded text-xs focus:border-indigo-500 focus:outline-none" /></td>
                        <td className="p-2"><input type="text" name="fatherRemarks" value={formData.fatherRemarks || ''} onChange={handleInputChange} className="w-full p-1.5 border border-gray-200 rounded text-xs focus:border-indigo-500 focus:outline-none" /></td>
                        <td className="p-2 text-center">
                          <input 
                            type="checkbox" 
                            name="fatherDeceased" 
                            checked={formData.fatherDeceased || false}
                            onChange={handleInputChange}
                            className="w-4 h-4 text-red-600 rounded"
                          />
                        </td>
                        <td className="p-2"></td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="p-2 font-medium text-sm">Mother</td>
                        <td className="p-2"><input type="text" name="motherName" value={formData.motherName || ''} onChange={handleInputChange} className="w-full p-1.5 border border-gray-200 rounded text-xs focus:border-indigo-500 focus:outline-none" /></td>
                        <td className="p-2"><input type="date" name="motherDOB" value={formData.motherDOB || ''} onChange={handleInputChange} className="w-full p-1.5 border border-gray-200 rounded text-xs focus:border-indigo-500 focus:outline-none" max="100" /></td>
                        <td className="p-2"><input type="text" name="motherEducation" value={formData.motherEducation || ''} onChange={handleInputChange} className="w-full p-1.5 border border-gray-200 rounded text-xs focus:border-indigo-500 focus:outline-none" /></td>
                        <td className="p-2"><input type="text" name="motherOccupation" value={formData.motherOccupation || ''} onChange={handleInputChange} className="w-full p-1.5 border border-gray-200 rounded text-xs focus:border-indigo-500 focus:outline-none" /></td>
                        <td className="p-2"><input type="text" name="motherLocation" value={formData.motherLocation || ''} onChange={handleInputChange} className="w-full p-1.5 border border-gray-200 rounded text-xs focus:border-indigo-500 focus:outline-none" /></td>
                        <td className="p-2"><input type="text" name="motherRemarks" value={formData.motherRemarks || ''} onChange={handleInputChange} className="w-full p-1.5 border border-gray-200 rounded text-xs focus:border-indigo-500 focus:outline-none" /></td>
                        <td className="p-2 text-center">
                          <input 
                            type="checkbox" 
                            name="motherDeceased" 
                            checked={formData.motherDeceased || false}
                            onChange={handleInputChange}
                            className="w-4 h-4 text-red-600 rounded"
                          />
                        </td>
                        <td className="p-2"></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="flex justify-center">
                <button
                  type="button"
                  onClick={addChild}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all transform hover:scale-105 font-medium text-sm"
                >
                  <Plus size={16} />
                  Add Another Child
                </button>
              </div>
            </div>
          )}

          {/* Step 4: Education */}
          {(currentStep === 4 || printState) && (
            <div className="pdf-break space-y-6 ">
              <h2 className="text-xl sm:text-2xl font-semibold text-gray-800 mb-6 pb-2 border-b-2 border-indigo-500">Education History</h2>
              
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                <label className="block text-gray-700 font-medium mb-2 text-sm">
                  If you haven't completed Class 10th, what is your lowest education level? <span className="text-red-500">*</span>
                </label>
                <select
                  name="lowestEducation"
                  value={formData.lowestEducation || ''}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-lg focus:border-indigo-500 focus:outline-none transition-colors bg-white text-sm"
                >
                  <option value="">Select if applicable</option>
                  <option value="never attended school">never attended school</option>
                  <option value="1st pass">1st pass</option>
                  <option value="2nd pass">2nd pass</option>
                  <option value="3rd pass">3rd pass</option>
                  <option value="4th pass">4th pass</option>
                  <option value="5th pass">5th pass</option>
                  <option value="6th pass">6th pass</option>
                  <option value="7th pass">7th pass</option>
                  <option value="8th pass">8th pass</option>
                  <option value="9th pass">9th pass</option>
                  <option value="others">others</option>
                </select>
              </div>
              
              <div className="overflow-x-auto">
                <div className="min-w-full bg-white rounded-lg shadow-lg overflow-hidden">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white">
                        <th className="p-3 text-left font-semibold text-sm">Level</th>
                        <th className="p-3 text-left font-semibold text-sm">Board/University</th>
                        <th className="p-3 text-left font-semibold text-sm">Year of Passing</th>
                        <th className="p-3 text-left font-semibold text-sm">Subject/Course</th>
                        <th className="p-3 text-left font-semibold text-sm">Marks/CGPA</th>
                        <th className="p-3 text-left font-semibold text-sm">Remarks</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        { level: 'Class X', prefix: 'class10' },
                        { level: 'Class XII', prefix: 'class12' },
                        { level: 'Diploma', prefix: 'diploma' },
                        { level: 'Graduation', prefix: 'graduation' },
                        { level: 'Post Graduation', prefix: 'pg' }
                      ].map(({ level, prefix }) => (
                        <tr key={prefix} className="border-b border-gray-200 hover:bg-gray-50">
                          <td className="p-2 font-medium text-sm">{level}</td>
                          <td className="p-2"><input type="text" name={`${prefix}Board`} value={formData[`${prefix}Board`] || ''} onChange={handleInputChange} className="w-full p-1.5 border border-gray-200 rounded text-xs focus:border-indigo-500 focus:outline-none" /></td>
                          <td className="p-2"><input type="number" name={`${prefix}Year`} value={formData[`${prefix}Year`] || ''} onChange={handleInputChange} className="w-full p-1.5 border border-gray-200 rounded text-xs focus:border-indigo-500 focus:outline-none" min="1950" max="2030" /></td>
                          <td className="p-2"><input type="text" name={`${prefix}Subject`} value={formData[`${prefix}Subject`] || ''} onChange={handleInputChange} className="w-full p-1.5 border border-gray-200 rounded text-xs focus:border-indigo-500 focus:outline-none" /></td>
                          <td className="p-2"><input type="text" name={`${prefix}Marks`} value={formData[`${prefix}Marks`] || ''} onChange={handleInputChange} className="w-full p-1.5 border border-gray-200 rounded text-xs focus:border-indigo-500 focus:outline-none" placeholder="%" /></td>
                          <td className="p-2"><input type="text" name={`${prefix}Remarks`} value={formData[`${prefix}Remarks`] || ''} onChange={handleInputChange} className="w-full p-1.5 border border-gray-200 rounded text-xs focus:border-indigo-500 focus:outline-none" /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <label className="block text-gray-700 font-medium mb-2 text-sm">Reasons for gap, if any, in education</label>
                  <textarea
                    name="educationGap"
                    value={formData.educationGap || ''}
                    onChange={handleInputChange}
                    rows="4"
                    className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-lg focus:border-indigo-500 focus:outline-none transition-colors bg-gray-50 focus:bg-white resize-vertical text-sm"
                    placeholder="Explain any gaps in your education timeline..."
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-2 text-sm">Academic achievements / Honors / Scholarships</label>
                  <textarea
                    name="achievements"
                    value={formData.achievements || ''}
                    onChange={handleInputChange}
                    rows="4"
                    className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-lg focus:border-indigo-500 focus:outline-none transition-colors bg-gray-50 focus:bg-white resize-vertical text-sm"
                    placeholder="List your academic achievements, awards, scholarships..."
                  />
                </div>
              </div>

              <div className="pdf-break grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <label className="block text-gray-700 font-medium mb-2 text-sm">Computer Packages/Languages Known</label>
                  <textarea
                    name="computerSkills"
                    value={formData.computerSkills || ''}
                    onChange={handleInputChange}
                    rows="4"
                    className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-lg focus:border-indigo-500 focus:outline-none transition-colors bg-gray-50 focus:bg-white resize-vertical text-sm"
                    placeholder="e.g., MS Office, Photoshop, Java, Python, etc."
                  />
                </div>
                <div>
                  <label className="block text-gray-700 font-medium mb-2 text-sm">Languages Known</label>
                  <textarea
                    name="languages"
                    value={formData.languages || ''}
                    onChange={handleInputChange}
                    rows="4"
                    className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-lg focus:border-indigo-500 focus:outline-none transition-colors bg-gray-50 focus:bg-white resize-vertical text-sm"
                    placeholder="Please specify: Speak, read & write / Speak & Read Only / Read & Write Only / Read Only / Speak Only"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 5: Work Experience */}
          {(currentStep === 5 || printState) && (
            <div className="  space-y-6 ">
              <h2 className="text-xl sm:text-2xl font-semibold text-gray-800 mb-6 pb-2 border-b-2 border-indigo-500">Work Experience</h2>
              
              {experiences.map((exp, index) => (
                <div key={exp.id} className="bg-gradient-to-r from-gray-50 to-blue-50 p-4 sm:p-6 rounded-lg border border-gray-200">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold text-gray-800">Experience {index + 1}</h3>
                    {experiences.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeExperience(exp.id)}
                        className="text-red-600 hover:text-red-800 p-1"
                        title="Remove Experience"
                      >
                        <Trash2 size={18} />
                      </button>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-gray-700 font-medium mb-2 text-sm">Organization Name</label>
                      <input
                        type="text"
                        value={exp.company}
                        onChange={(e) => handleExperienceChange(exp.id, 'company', e.target.value)}
                        className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-lg focus:border-indigo-500 focus:outline-none transition-colors bg-white text-sm"
                        placeholder="Enter company name"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-gray-700 font-medium mb-2 text-sm">Position/Designation</label>
                      <input
                        type="text"
                        value={exp.position}
                        onChange={(e) => handleExperienceChange(exp.id, 'position', e.target.value)}
                        className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-lg focus:border-indigo-500 focus:outline-none transition-colors bg-white text-sm"
                        placeholder="Enter your position"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-gray-700 font-medium mb-2 text-sm">Industry</label>
                      <select
                        value={exp.industry}
                        onChange={(e) => handleExperienceChange(exp.id, 'industry', e.target.value)}
                        className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-lg focus:border-indigo-500 focus:outline-none transition-colors bg-white text-sm"
                      >
                        <option value="">Select Industry</option>
                        <option value="IT/Software">IT/Software</option>
                        <option value="Banking/Financial Services">Banking/Financial Services</option>
                        <option value="Manufacturing">Manufacturing</option>
                        <option value="Healthcare">Healthcare</option>
                        <option value="Education">Education</option>
                        <option value="Retail">Retail</option>
                        <option value="Construction">Construction</option>
                        <option value="Telecommunications">Telecommunications</option>
                        <option value="Automotive">Automotive</option>
                        <option value="Real Estate">Real Estate</option>
                        <option value="Food & Beverage">Food & Beverage</option>
                        <option value="Media & Entertainment">Media & Entertainment</option>
                        <option value="Transportation & Logistics">Transportation & Logistics</option>
                        <option value="Government">Government</option>
                        <option value="NGO/Non-Profit">NGO/Non-Profit</option>
                        <option value="Agriculture">Agriculture</option>
                        <option value="Energy">Energy</option>
                        <option value="Tourism">Tourism</option>
                        <option value="Consulting">Consulting</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
                    <div>
                      <label className="block text-gray-700 font-medium mb-2 text-sm">CTC Annual (₹)</label>
                      <input
                        type="text"
                        value={exp.salary}
                        onChange={(e) => handleExperienceChange(exp.id, 'salary', e.target.value)}
                        className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-lg focus:border-indigo-500 focus:outline-none transition-colors bg-white text-sm"
                        placeholder="Enter salary amount"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-gray-700 font-medium mb-2 text-sm">Date Of Joining</label>
                      <input
                        type="date"
                        value={exp.inputStartDate}
                        onChange={(e) => {
                          const formatted = formatDateToDDMMYYYY(e.target.value);
                          handleExperienceChange(exp.id, 'inputStartDate', e.target.value);
                          handleExperienceChange(exp.id, 'startDate', formatted);
                          handleExperienceChange(exp.id, 'startDateISO', e.target.value);
                        }}
                        className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-lg focus:border-indigo-500 focus:outline-none transition-colors bg-white text-sm"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-gray-700 font-medium mb-2 text-sm">Date Of Leaving</label>
                      <input
                        type="date"
                        value={exp.inputEndDate}
                        onChange={(e) => {
                          const formatted = formatDateToDDMMYYYY(e.target.value);
                          handleExperienceChange(exp.id, 'inputEndDate', e.target.value);
                          handleExperienceChange(exp.id, 'endDate', formatted);
                          handleExperienceChange(exp.id, 'endDateISO', e.target.value);
                        }}
                        className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-lg focus:border-indigo-500 focus:outline-none transition-colors bg-white text-sm"
                      />
                    </div>
                  </div>

                  {exp.startDate && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-4">
                      <p className="text-blue-800 text-sm">
                        Duration: {exp.startDate} to {exp.endDate || 'Present'}
                      </p>
                    </div>
                  )}
                  
                  <div className="mt-4">
                    <label className="block text-gray-700 font-medium mb-2 text-sm">Reason for Leaving</label>
                    <input
                      type="text"
                      value={exp.reasonForLeaving}
                      onChange={(e) => handleExperienceChange(exp.id, 'reasonForLeaving', e.target.value)}
                      className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-lg focus:border-indigo-500 focus:outline-none transition-colors bg-white text-sm"
                      placeholder="Reason for leaving the position"
                    />
                  </div>
                  
                  <div className="mt-4">
                    <label className="block text-gray-700 font-medium mb-2 text-sm">Key Responsibilities</label>
                    <textarea
                      value={exp.responsibilities}
                      onChange={(e) => handleExperienceChange(exp.id, 'responsibilities', e.target.value)}
                      rows="3"
                      className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-lg focus:border-indigo-500 focus:outline-none transition-colors bg-white resize-vertical text-sm"
                      placeholder="Describe your key responsibilities and achievements..."
                    />
                  </div>
                </div>
              ))}
              
              <div className="flex justify-center">
                <button
                  type="button"
                  onClick={addExperience}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all transform hover:scale-105 font-medium text-sm"
                >
                  <Plus size={16} />
                  Add Work Experience
                </button>
              </div>
              
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-sm text-gray-700">
                  <strong>Note:</strong> If you are a fresher with no work experience, you can leave the fields empty or mention any internships, part-time jobs, or volunteer work.
                </p>
              </div>
            </div>
          )}

          {/* Step 6: References */}
          {(currentStep === 6 || printState) && (
  <div className="  space-y-6 ">
    <h2 className="text-xl sm:text-2xl font-semibold text-gray-800 mb-6 pb-2 border-b-2 border-indigo-500">References</h2>
    
    {/* Professional References Section */}
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-700 flex items-center gap-2">
        <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">Professional References</span>
      </h3>
      {/* description */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-gray-700">
          <strong>Note:</strong> Please give three professional references not related to you, who hold responsible position in the line
of your occupation (preferably present or previous employers) who can testify your professional
capabilities. (In case you are a fresher, please share details of a senior person (Principal, Dean,
Professors) from school / college
        </p>
      </div>
      
      {professionalReferences.length === 0 ? (
        <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
          <p className="text-gray-500 text-sm">No professional references added yet</p>
        </div>
      ) : (
        professionalReferences.map((ref, index) => (
          <div key={ref.id} className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 sm:p-6 rounded-lg border border-blue-200">
            <div className="flex justify-between items-center mb-4">
              <h4 className="text-lg font-semibold text-gray-800">Professional Reference {index + 1}</h4>
              <button
                type="button"
                onClick={() => removeReference(ref.id)}
                className="text-red-600 hover:text-red-800 p-1"
                title="Remove Reference"
              >
                <Trash2 size={18} />
              </button>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-gray-700 font-medium mb-2 text-sm">Full Name</label>
                <input
                  type="text"
                  value={ref.name}
                  onChange={(e) => handleReferenceChange(ref.id, 'name', e.target.value)}
                  className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-lg focus:border-indigo-500 focus:outline-none transition-colors bg-white text-sm"
                  placeholder="Enter full name"
                />
              </div>
              
              <div>
                <label className="block text-gray-700 font-medium mb-2 text-sm">Position/Designation</label>
                <input
                  type="text"
                  value={ref.position}
                  onChange={(e) => handleReferenceChange(ref.id, 'position', e.target.value)}
                  className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-lg focus:border-indigo-500 focus:outline-none transition-colors bg-white text-sm"
                  placeholder="Enter position"
                />
              </div>
              
              <div>
                <label className="block text-gray-700 font-medium mb-2 text-sm">Company/Organization</label>
                <input
                  type="text"
                  value={ref.company}
                  onChange={(e) => handleReferenceChange(ref.id, 'company', e.target.value)}
                  className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-lg focus:border-indigo-500 focus:outline-none transition-colors bg-white text-sm"
                  placeholder="Enter company name"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
              <div>
                <label className="block text-gray-700 font-medium mb-2 text-sm">Phone Number</label>
                <input
                  type="tel"
                  value={ref.phone}
                  onChange={(e) => handleReferenceChange(ref.id, 'phone', e.target.value)}
                  className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-lg focus:border-indigo-500 focus:outline-none transition-colors bg-white text-sm"
                  placeholder="Enter phone number"
                  maxLength="10"
                />
              </div>
              
              <div>
                <label className="block text-gray-700 font-medium mb-2 text-sm">Email Address</label>
                <input
                  type="email"
                  value={ref.email}
                  onChange={(e) => handleReferenceChange(ref.id, 'email', e.target.value)}
                  className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-lg focus:border-indigo-500 focus:outline-none transition-colors bg-white text-sm"
                  placeholder="Enter email address"
                />
              </div>
              
              <div>
                <label className="block text-gray-700 font-medium mb-2 text-sm">Relationship</label>
                <select
                  value={ref.relationship}
                  onChange={(e) => handleReferenceChange(ref.id, 'relationship', e.target.value)}
                  className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-lg focus:border-indigo-500 focus:outline-none transition-colors bg-white text-sm"
                >
                  <option value="">Select relationship</option>
                  <option value="supervisor">Former Supervisor</option>
                  <option value="manager">Former Manager</option>
                  <option value="colleague">Former Colleague</option>
                  <option value="client">Former Client</option>
                  <option value="business-partner">Business Partner</option>
                  <option value="other">Other Professional</option>
                </select>
              </div>
            </div>
          </div>
        ))
      )}
      
      <div className="flex justify-center">
        <button
          type="button"
          onClick={addProfessionalReference}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all transform hover:scale-105 font-medium text-sm"
        >
          <Plus size={16} />
          Add Professional Reference
        </button>
      </div>
    </div>

    {/* Personal References Section */}
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-700 flex items-center gap-2">
        <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm">Personal References</span>
      </h3>

      {/* description */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <p className="text-sm text-gray-700">
          <strong>Note:</strong> Please give reference of two responsible persons of your locality to whom you are known :
        </p>
      </div>
      
      {personalReferences.length === 0 ? (
        <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
          <p className="text-gray-500 text-sm">No personal references added yet</p>
        </div>
      ) : (
        personalReferences.map((ref, index) => (
          <div key={ref.id} className="bg-gradient-to-r from-green-50 to-teal-50 p-4 sm:p-6 rounded-lg border border-green-200">
            <div className="flex justify-between items-center mb-4">
              <h4 className="text-lg font-semibold text-gray-800">Personal Reference {index + 1}</h4>
              <button
                type="button"
                onClick={() => removeReference(ref.id)}
                className="text-red-600 hover:text-red-800 p-1"
                title="Remove Reference"
              >
                <Trash2 size={18} />
              </button>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-gray-700 font-medium mb-2 text-sm">Full Name</label>
                <input
                  type="text"
                  value={ref.name}
                  onChange={(e) => handleReferenceChange(ref.id, 'name', e.target.value)}
                  className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-lg focus:border-indigo-500 focus:outline-none transition-colors bg-white text-sm"
                  placeholder="Enter full name"
                />
              </div>
              
              <div>
                <label className="block text-gray-700 font-medium mb-2 text-sm">Position/Designation</label>
                <input
                  type="text"
                  value={ref.position}
                  onChange={(e) => handleReferenceChange(ref.id, 'position', e.target.value)}
                  className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-lg focus:border-indigo-500 focus:outline-none transition-colors bg-white text-sm"
                  placeholder="Enter position (optional)"
                />
              </div>
              
              <div>
                <label className="block text-gray-700 font-medium mb-2 text-sm">Organization (if any)</label>
                <input
                  type="text"
                  value={ref.company}
                  onChange={(e) => handleReferenceChange(ref.id, 'company', e.target.value)}
                  className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-lg focus:border-indigo-500 focus:outline-none transition-colors bg-white text-sm"
                  placeholder="Enter organization (optional)"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
              <div>
                <label className="block text-gray-700 font-medium mb-2 text-sm">Phone Number</label>
                <input
                  type="tel"
                  value={ref.phone}
                  onChange={(e) => handleReferenceChange(ref.id, 'phone', e.target.value)}
                  className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-lg focus:border-indigo-500 focus:outline-none transition-colors bg-white text-sm"
                  placeholder="Enter phone number"
                  maxLength="10"
                />
              </div>
              
              <div>
                <label className="block text-gray-700 font-medium mb-2 text-sm">Email Address</label>
                <input
                  type="email"
                  value={ref.email}
                  onChange={(e) => handleReferenceChange(ref.id, 'email', e.target.value)}
                  className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-lg focus:border-indigo-500 focus:outline-none transition-colors bg-white text-sm"
                  placeholder="Enter email address"
                />
              </div>
              
              <div>
                <label className="block text-gray-700 font-medium mb-2 text-sm">Relationship</label>
                <select
                  value={ref.relationship}
                  onChange={(e) => handleReferenceChange(ref.id, 'relationship', e.target.value)}
                  className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-lg focus:border-indigo-500 focus:outline-none transition-colors bg-white text-sm"
                >
                  <option value="">Select relationship</option>
                  <option value="friend">Friend</option>
                  <option value="mentor">Personal Mentor</option>
                  <option value="community-leader">Community Leader</option>
                  <option value="volunteer-coordinator">Volunteer Coordinator</option>
                  <option value="professor">Professor/Teacher</option>
                  <option value="other">Other Personal</option>
                </select>
              </div>
            </div>
          </div>
        ))
      )}
      
      <div className="flex justify-center">
        <button
          type="button"
          onClick={addPersonalReference}
          className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all transform hover:scale-105 font-medium text-sm"
        >
          <Plus size={16} />
          Add Personal Reference
        </button>
      </div>
    </div>
    
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
      <p className="text-sm text-gray-700">
        <strong>Note:</strong> 
        <br />• <strong>Professional references</strong> should be former supervisors, colleagues, clients, or business contacts who can speak to your work abilities.
        <br />• <strong>Personal references</strong> can be friends, mentors, community leaders, or others who know your character (avoid family members).
      </p>
    </div>
  </div>
)}

          {/* Step 7: Declaration */}
          {(currentStep === 7 || printState) && (
            <div className="  space-y-6 ">
              <h2 className="text-xl sm:text-2xl font-semibold text-gray-800 mb-6 pb-2 border-b-2 border-indigo-500">Declaration</h2>

              {/* info */}
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

                {/* <div>
                  <label className="block text-gray-700 font-medium mb-2 text-sm">Hobbies & Interests</label>
                  <textarea
                    name="hobbies"
                    value={formData.hobbies || ''}
                    onChange={handleInputChange}
                    rows="4"
                    className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-lg focus:border-indigo-500 focus:outline-none transition-colors bg-gray-50 focus:bg-white resize-vertical text-sm"
                    placeholder="List your hobbies, interests, and extracurricular activities..."
                  />
                </div> */}

                {/* <div>
                  <label className="block text-gray-700 font-medium mb-2 text-sm">Strengths</label>
                  <textarea
                    name="strengths"
                    value={formData.strengths || ''}
                    onChange={handleInputChange}
                    rows="4"
                    className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-lg focus:border-indigo-500 focus:outline-none transition-colors bg-gray-50 focus:bg-white resize-vertical text-sm"
                    placeholder="Describe your key strengths and qualities..."
                  />
                </div> */}
              </div>

                <span className="text-sm text-gray-700 font-bold">If you are related to any current employee of our organization, please provide the following</span>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Not Applicable checkbox to hide all fields */}
                <div className="flex items-center justify-start gap-2">
                  <input type="checkbox" name="notApplicable" checked={formData.notApplicable} onChange={handleInputChange} />
                  <label className="block text-gray-700 font-medium text-sm">Not Applicable</label>
                </div>
                {!formData.notApplicable && <><div>
                  <label className="block text-gray-700 font-medium mb-2 text-sm">Name of the related employee</label>
                  <input
                    name="stateName"
                    value={formData.stateName || ''}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-lg focus:border-indigo-500 focus:outline-none transition-colors bg-gray-50 focus:bg-white resize-vertical text-sm"
                    placeholder="Enter Name of the related employee"
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-2 text-sm">Department</label>
                  <div className="relative">
                    <input
                      type="text"
                      name="department"
                      value={formData.department || ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        setFormData(prev => ({ ...prev, department: value }));
                      }}
                      className="w-full pl-3 pr-3 py-2.5 border-2 border-gray-200 rounded-lg focus:border-indigo-500 focus:outline-none transition-colors bg-gray-50 focus:bg-white text-sm"
                      placeholder="Enter department"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-gray-700 font-medium mb-2 text-sm">Designation</label>
                  <div className="relative">
                    <input
                      type="text"
                      name="designation"
                      value={formData.designation || ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        setFormData(prev => ({ ...prev, designation: value }));
                      }}
                      className="w-full pl-3 pr-3 py-2.5 border-2 border-gray-200 rounded-lg focus:border-indigo-500 focus:outline-none transition-colors bg-gray-50 focus:bg-white text-sm"
                      placeholder="Enter designation"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-2 text-sm">Location</label>
                  <select
                    name="workLocation"
                    value={formData.workLocation || ''}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-lg focus:border-indigo-500 focus:outline-none transition-colors bg-gray-50 focus:bg-white text-sm"
                  >
                    <option value="">Select location</option>
                    <option value="delhi">Delhi NCR</option>
                    <option value="mumbai">Mumbai</option>
                    <option value="bangalore">Bangalore</option>
                    <option value="pune">Pune</option>
                    <option value="chennai">Chennai</option>
                    <option value="kolkata">Kolkata</option>
                    <option value="hyderabad">Hyderabad</option>
                    <option value="ahmedabad">Ahmedabad</option>
                    <option value="any">Any Location</option>
                    <option value="remote">Remote Work</option>
                  </select>
                </div></>}
              </div>

              

              {/* <div>
                <label className="block text-gray-700 font-medium mb-2 text-sm">Additional Comments</label>
                <textarea
                  name="additionalComments"
                  value={formData.additionalComments || ''}
                  onChange={handleInputChange}
                  rows="4"
                  className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-lg focus:border-indigo-500 focus:outline-none transition-colors bg-gray-50 focus:bg-white resize-vertical text-sm"
                  placeholder="Any additional information you'd like to share..."
                />
              </div> */}

              {/* add a line here  */}
              <div className='border-b-2 border-gray-500'></div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* First row (2 fields on md+, 1 field per row on mobile) */}
                <div>
                  <label className="block text-gray-700 font-medium mb-2 text-sm">
                    List any physical disabilities:
                  </label>
                  <input
                    type="text"
                    name="physicalDisabilities"
                    required
                    value={formData.physicalDisabilities}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-lg 
                 focus:border-indigo-500 focus:outline-none transition-colors bg-white text-sm"
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-2 text-sm">
                    Last prolonged serious illness / major operation undergone:
                  </label>
                  <input
                    type="text"
                    name="lastProlongedSeriousIllness"
                    required
                    value={formData.lastProlongedSeriousIllness}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-lg 
                 focus:border-indigo-500 focus:outline-none transition-colors bg-white text-sm"
                  />
                </div>

                {/* Second row (3 fields on md+, stack vertically on mobile) */}
                <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-gray-700 font-medium mb-2 text-sm">
                      Have you ever been tried in court?
                    </label>
                    <select
                      name="haveYouEverBeenTriedInCourt"
                      required
                      value={formData.haveYouEverBeenTriedInCourt}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-lg 
                   focus:border-indigo-500 focus:outline-none transition-colors bg-white text-sm"
                    >
                      <option value="">Select</option>
                      <option value="yes">Yes</option>
                      <option value="no">No</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-gray-700 font-medium mb-2 text-sm">
                      Have you ever been in police custody/ arrested?
                    </label>
                    <select
                      name="haveYouEverBeenInPoliceCustody"
                      required
                      value={formData.haveYouEverBeenInPoliceCustody}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-lg 
                   focus:border-indigo-500 focus:outline-none transition-colors bg-white text-sm"
                    >
                      <option value="">Select</option>
                      <option value="yes">Yes</option>
                      <option value="no">No</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-gray-700 font-medium mb-2 text-sm">
                      Is there any court proceeding / police enquiry pending against you?
                    </label>
                    <select
                      name="isThereAnyCourtProceeding"
                      required
                      value={formData.isThereAnyCourtProceeding}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-lg 
                   focus:border-indigo-500 focus:outline-none transition-colors bg-white text-sm"
                    >
                      <option value="">Select</option>
                      <option value="yes">Yes</option>
                      <option value="no">No</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-purple-50 to-indigo-50 p-6 rounded-lg border border-purple-200">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Declaration</h3>
                <div className="space-y-3">
                  <label className="flex items-start space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      name="declaration1"
                      checked={formData.declaration1 || false}
                      onChange={handleInputChange}
                      className="w-4 h-4 text-indigo-600 mt-1 rounded"
                      required
                    />
                    <span className="text-sm text-gray-700">
                      I hereby declare that all the information provided above is true and accurate to the best of my knowledge.
                    </span>
                  </label>
                  
                  <label className="flex items-start space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      name="declaration2"
                      checked={formData.declaration2 || false}
                      onChange={handleInputChange}
                      className="w-4 h-4 text-indigo-600 mt-1 rounded"
                      required
                    />
                    <span className="text-sm text-gray-700">
                      I understand that any false information may lead to the rejection of my application or termination of employment.
                    </span>
                  </label>
                  
                  <label className="flex items-start space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      name="declaration3"
                      checked={formData.declaration3 || false}
                      onChange={handleInputChange}
                      className="w-4 h-4 text-indigo-600 mt-1 rounded"
                      required
                    />
                    <span className="text-sm text-gray-700">
                      I consent to the processing of my personal data for recruitment purposes in accordance with applicable data protection laws.
                    </span>
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Step 8: Review & Submit */}
          {currentStep === 8 && (
            <div className="  space-y-8">
              <div className="text-center py-12 bg-gradient-to-r from-green-50 to-blue-50 rounded-lg border border-green-200">
                <div className="text-6xl mb-6">🎉</div>
                <h2 className="text-2xl sm:text-3xl font-bold text-green-600 mb-4">Application Submitted Successfully!</h2>
                <p className="text-gray-600 mb-2 text-sm sm:text-base">Thank you for submitting your personal data form to Kajaria Group.</p>
                <p className="text-gray-600 text-sm sm:text-base">We will review your application and contact you within 7-10 business days.</p>
                
                <div className="mt-8 p-4 bg-white rounded-lg inline-block shadow-lg">
                  <p className="text-sm text-gray-700">
                    <strong>Application ID:</strong> {finalData.applicationId}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">Please save this ID for future reference</p>
                </div>

                <div className="mt-8 space-x-4">
                  <button
                    onClick={() =>  setPrintState(true)}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm"
                  >
                    Print Application
                  </button>
                  
                  <button
                    onClick={() => {
                      localStorage.removeItem('formAutoSaveData');
                      window.location.reload();
                    }}
                    className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors font-medium text-sm"
                  >
                    Submit Another Application
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          {currentStep < 8 && (
            <div className="no-print flex flex-col sm:flex-row justify-between items-center mt-8 gap-4">
              <div className="flex items-center gap-4">
                <button
                  type="button"
                  onClick={() => changeStep(-1)}
                  className={`px-6 sm:px-8 py-3 rounded-lg font-semibold text-sm uppercase tracking-wide transition-all ${
                    currentStep === 1
                      ? 'hidden'
                      : 'bg-gray-500 text-white hover:bg-gray-600 hover:transform hover:-translate-y-1 shadow-lg'
                  }`}
                >
                  ← Previous
                </button>
                
                <button
                  type="button"
                  onClick={saveToStorage}
                  className="flex items-center gap-2 px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-all text-sm font-medium"
                  title="Save Progress"
                >
                  <Save size={16} />
                  Save
                </button>
                <button
                  type="button"
                  onClick={() => setPrintState(true)}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm"
                >
                  Print Application
                </button>
              </div>
              
              <div className="text-center">
                <div className="text-xs text-gray-500 mb-2">
                  Step {currentStep} of {totalSteps}
                </div>
                <button
                  type="button"
                  onClick={() => changeStep(1)}
                  // disabled={currentStep>7?false:shouldDisableInput}
                  className="px-6 sm:px-8 py-3 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-lg font-semibold text-sm uppercase tracking-wide hover:from-indigo-600 hover:to-purple-700 hover:transform hover:-translate-y-1 transition-all shadow-lg hover:shadow-indigo-500/25"
                >
                  {currentStep === totalSteps - 1 ? 'Submit Application' : 'Next →'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const InputField = ({ label, name, type = "text", required = false, placeholder = "", className = "", validationErrors, formData, handleInputChange, disabled = false,  ...props }) => {
  const { user, logout } = useAuth();
  const shouldDisableInput = user.filledForms.includes("Personal")
  return (
    <div className={className}>
        <div className="flex items-center gap-2 justify-between mb-2">
            <label className="block text-gray-700 font-medium mb-2 text-sm">
                {label} {required && <span className="text-red-500">*</span>}
            </label>
            {label === "Height (cm)"&& <span className="text-gray-500 text-xs">In inches: {(formData[name] / 2.54).toFixed(2)=="NaN"?"":(formData[name] / 2.54).toFixed(2)}</span>}
        </div>
      <input
        type={type}
        name={name}
        value={formData[name] || ''}
        onChange={handleInputChange}
        placeholder={placeholder}
        disabled={shouldDisableInput}
        className={`w-full px-3 py-2.5 border-2 rounded-lg focus:outline-none transition-all text-sm ${
          disabled 
            ? 'bg-gray-100 cursor-not-allowed border-gray-200'
            : validationErrors[name]
            ? 'border-red-300 focus:border-red-500 bg-red-50'
            : 'border-gray-200 focus:border-indigo-500 bg-gray-50 focus:bg-white'
        }`}
        required={required}
        {...props}
      />
      {validationErrors[name] && (
        <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
          <AlertCircle size={12} />
          {validationErrors[name]}
        </p>
      )}
    </div>
  );
}

export default PersonalDataForm;